/**
 * capability.ts — the single authorisation decision point
 * (ADR-20260821-m1sn).
 *
 * SUPERSEDES `m1-portal-routing-resume-model`'s self-authored stub at
 * this same path (ADR-20260921-hnn5): that stub's action-set-shaped
 * resolver and its per-section action-string builder were retired by
 * `m1-portal-capability-boundary` TG-11, which migrated the stub's eight
 * consumers and `use-capability.ts` onto the `Capability` shape below
 * (ADR-20261007-r3vw). TG-11 ran after TG-4, in wave 3; this task group
 * (TG-1, wave 1) landed the real resolver first, which is why
 * `use-capability.ts` and the stub's own flat test file (both stub
 * artefacts, neither touched by TG-1) reported compile/test failures
 * between TG-1 landing and TG-11 landing — a known, planned transition,
 * not a regression introduced here. TG-11 has since deleted that stub
 * test file and migrated every consumer onto this module directly.
 *
 * `resolveCapability` is the ONLY function in the repository that reads
 * a decoded claim's principal role and branches on it — proved by
 * `__tests__/containment.test.ts`'s repository-wide search (AC-1).
 */
import { decodeClaim } from "./claim";

/**
 * ADR-20260826-c1b3: two booleans for two distinct obligations
 * (US-SBC-004 submission, US-SBC-009 the truthfulness declaration) plus a
 * `ReadonlySet` of confirmable section identifiers — a set is assertable
 * in a test and serialisable into a fixture, where a predicate closure is
 * neither. No field carries the role, or anything from which the role
 * can be reconstructed: `__tests__/capability.test.ts` asserts this
 * object's key set against a declared allowlist.
 */
export interface Capability {
  readonly canSubmitRenewal: boolean;
  readonly canMakeTruthfulnessDeclaration: boolean;
  readonly confirmableSectionIds: ReadonlySet<string>;
}

const OWNER_ROLE = "owner";

/**
 * `Object.freeze` on a plain object makes its own properties non-writable
 * and non-configurable, but it does not touch a `Set`'s mutator methods --
 * `Set.prototype.add`/`delete`/`clear` operate on the instance's internal
 * `[[SetData]]` slot, not through an ordinary property write, so a frozen
 * `Set` still accepts `.add()` (confirmed: `Object.freeze(new
 * Set()).add(1)` succeeds in V8/Node). `readonly`/`ReadonlySet` is a
 * TypeScript compile-time view only and is erased at runtime, so neither
 * freeze nor the type stops a caller holding a live reference -- e.g. via
 * `as Set<string>` -- from mutating the set in place (OBJ-S-36, FIND-628).
 *
 * The first fix shadowed the three mutator methods as own, non-configurable
 * properties on the `Set` instance itself. That closed `instance.add(...)`
 * (ordinary property lookup finds the shadow), but a caller can still
 * bypass an own-property shadow entirely by invoking the prototype method
 * directly against the instance -- `Set.prototype.add.call(instance, x)` or
 * `Reflect.apply(Set.prototype.add, instance, [x])` -- because that
 * dispatch never consults the instance's own properties at all; it reads
 * the internal `[[SetData]]` slot straight off whatever object is passed as
 * `this`, and a real `Set` instance (its prototype is `Set.prototype`)
 * always has one (OBJ-S-36, FIND-635, turn 2 re-raise). Reproduced:
 * `Set.prototype.add.call(frozen, "x")` still mutated the instance under
 * the first fix.
 *
 * The only way to close that is to stop handing consumers a real `Set`
 * instance at all. This returns a plain, frozen object -- prototype
 * `Object.prototype`, not `Set.prototype` -- that implements the
 * `ReadonlySet` surface (`size`, `has`, `forEach`, `keys`, `values`,
 * `entries`, `[Symbol.iterator]`) by delegating reads to a private `Set`
 * closed over in this function's scope and never returned. Because the
 * returned object has no `[[SetData]]` slot of its own,
 * `Set.prototype.add.call(wrapper, x)` throws `TypeError: Method
 * Set.prototype.add called on incompatible receiver` instead of mutating
 * anything -- there is no mutator method to shadow or omit, because there
 * is no `Set` to call it on.
 *
 * OBJ-S-37 (FIND-645, turn 3): every read above was still written as
 * `data.has(value)` / `data.forEach(...)` / `data.keys()` etc. -- an
 * ordinary property lookup on `data` at call time, which resolves through
 * the shared, writable global `Set.prototype` unless `data` carries its
 * own shadowing property (it does not). A feature module that reassigns
 * `Set.prototype.has` (or `.forEach`/`.keys`/`.values`/`.entries`/
 * `[Symbol.iterator]`) at module scope -- no import of this module, no
 * cast, no claim reference required -- changes what every wrapper this
 * function has ever returned reports, including the shared
 * `DENIED_CAPABILITY` instance. Fixed by capturing each intrinsic method
 * off `Set.prototype` once, at this module's own evaluation time, into a
 * local, uncurried binding, and dispatching through that captured
 * reference rather than through a live property read on `data`. A later
 * reassignment of the global `Set.prototype` cannot reach a value already
 * copied into a local variable closed over here.
 *
 * OBJ-S-37 fix incomplete (turn 4, FIND-650): the turn-3 fix above captured
 * each method off `Set.prototype`, but every captured reference was still
 * *invoked* with `.call(data, ...)` -- `.call` is itself an ordinary
 * property read on the shared, writable `Function.prototype`, so a feature
 * module reassigning `Function.prototype.call` could substitute its own
 * dispatch for every captured method at once, and `get size() { return
 * data.size; }` was never captured at all and still read `Set.prototype`'s
 * `size` accessor live on every call. Closed by binding `Function.prototype
 * .call` itself into each captured method at module-evaluation time
 * (`uncurryThis`, the standard intrinsic-capture idiom: `fn.bind(fn.call)`
 * takes `.call`'s own *current* value once and produces a plain function
 * that no longer performs a `.call` property read at invocation time), and
 * by capturing the `size` accessor's getter the same way instead of
 * reading `data.size` through a live getter lookup.
 */
const uncurryThis = Function.prototype.bind.bind(Function.prototype.call) as <
  T extends (...args: never[]) => unknown,
>(
  fn: T,
) => (thisArg: ThisParameterType<T>, ...args: Parameters<T>) => ReturnType<T>;

const setHas = uncurryThis(Set.prototype.has);
const setForEach = uncurryThis(Set.prototype.forEach);
const setKeys = uncurryThis(Set.prototype.keys);
const setValues = uncurryThis(Set.prototype.values);
const setEntries = uncurryThis(Set.prototype.entries);
const setIterator = uncurryThis(Set.prototype[Symbol.iterator]);
const setSizeGetter = uncurryThis(
  Object.getOwnPropertyDescriptor(Set.prototype, "size")!.get as (this: Set<unknown>) => number,
);

/**
 * OBJ-S-37 fix incomplete again (turn 5, FIND-660): `forEach`'s callback dispatch below still read
 * `callback.call(...)` as an ordinary property lookup on the *consumer-supplied* callback, through
 * the same shared, writable `Function.prototype` the uncurried methods above were captured to stop
 * depending on. Closed the same way: `functionCall`, bound off `Function.prototype.call`'s value at
 * this module's own evaluation time, replaces the live `.call` property read at invocation time.
 *
 * OBJ-S-38 (FIND-658): `frozenConfirmableSectionIds` and `freezeCapability` below both called the
 * global `Object.freeze` through a live property read (`Object.freeze(...)`) at every
 * `resolveCapability` call, not only at module evaluation — a feature module reassigning
 * `Object.freeze` at module scope (`Object.freeze = (o) => o`) left every subsequently resolved
 * `Capability`, including the shared `DENIED_CAPABILITY`, silently mutable, with every existing
 * static check (`tsc`, `eslint`, dependency-cruiser, both containment scans) silent. `objectFreeze`
 * captures the real `Object.freeze` once, here, the same way the `Set.prototype` methods above are
 * captured, so a later reassignment of the global cannot reach the reference this module actually
 * calls.
 */
const functionCall = uncurryThis(Function.prototype.call) as (
  fn: (...args: never[]) => unknown,
  thisArg: unknown,
  ...args: unknown[]
) => unknown;
const objectFreeze = Object.freeze;

function frozenConfirmableSectionIds(ids: Iterable<string>): ReadonlySet<string> {
  const data = new Set(ids);
  const wrapper: ReadonlySet<string> = {
    get size() {
      return setSizeGetter(data);
    },
    has: (value: string) => setHas(data, value),
    forEach: (callback, thisArg) => {
      setForEach(data, (value) => functionCall(callback, thisArg, value, value, wrapper));
    },
    keys: () => setKeys(data),
    values: () => setValues(data),
    entries: () => setEntries(data),
    [Symbol.iterator]: () => setIterator(data),
  };
  return objectFreeze(wrapper);
}

/**
 * Freezes a resolved {@link Capability} so that neither its booleans nor
 * its `confirmableSectionIds` set can be mutated through a live reference
 * held by a consumer across re-renders (OBJ-S-36, FIND-628): `Object.freeze`
 * on the returned object makes `canSubmitRenewal`/`canMakeTruthfulnessDeclaration`
 * non-writable, so `Object.assign(capability, {...})` throws in this
 * module's strict-mode (ES module) context instead of silently escalating
 * a contributor to owner-shaped capability; {@link frozenConfirmableSectionIds}
 * does the equivalent for the set, via a non-`Set`-prototype wrapper object
 * rather than a frozen `Set` instance (OBJ-S-36, FIND-635).
 */
function freezeCapability(capability: {
  canSubmitRenewal: boolean;
  canMakeTruthfulnessDeclaration: boolean;
  confirmableSectionIds: Iterable<string>;
}): Capability {
  return objectFreeze({
    canSubmitRenewal: capability.canSubmitRenewal,
    canMakeTruthfulnessDeclaration: capability.canMakeTruthfulnessDeclaration,
    confirmableSectionIds: frozenConfirmableSectionIds(capability.confirmableSectionIds),
  });
}

const DENIED_CAPABILITY: Capability = freezeCapability({
  canSubmitRenewal: false,
  canMakeTruthfulnessDeclaration: false,
  confirmableSectionIds: new Set<string>(),
});

/**
 * Resolves a raw session claim into a {@link Capability}. Deny by default
 * on an absent, schema-invalid, role-missing or unrecognised-role claim
 * (ADR-20260826-82qe) — each of those four cases returns
 * {@link DENIED_CAPABILITY}, never a thrown exception a caller might
 * swallow into "permitted". An owner may submit the renewal and make the
 * truthfulness declaration; a contributor may do neither, under any
 * delegation, including one that names every section in the route table.
 */
export function resolveCapability(claim: Parameters<typeof decodeClaim>[0]): Capability {
  const decoded = decodeClaim(claim);
  if (decoded === null) {
    return DENIED_CAPABILITY;
  }

  const confirmableSectionIds = decoded.delegatedSectionIds;
  if (decoded.principalRole === OWNER_ROLE) {
    return freezeCapability({
      canSubmitRenewal: true,
      canMakeTruthfulnessDeclaration: true,
      confirmableSectionIds,
    });
  }

  return freezeCapability({
    canSubmitRenewal: false,
    canMakeTruthfulnessDeclaration: false,
    confirmableSectionIds,
  });
}
